import { useEffect, useRef, useState } from 'react';
import { useInterval } from '@toss/react';
import { safeLocalStorage } from '@toss/storage';
import { BroadcastChannel } from 'broadcast-channel';
import { useRouter } from 'next/router';

import { ping } from '@/api/gen/ping/ping.gen';
import { isDevelopment, isProduction, isStage } from '@/consts/env';
import { isZepQuizApiError } from '@/shared/error';
import { type UserInfo } from '@/types';

export const SESSION_KEY = 'zep-quiz-session-key';
const DUPLICATE_LOGIN_ACTIVATED_KEY = 'duplicate-login-activated';
const DUPLICATE_LOGIN_BROADCAST_CHANNEL = 'zep-quiz-duplicate-login';
const PING_INTERVAL = isProduction || isStage ? 1000 * 60 * 2 : 5000; // 라이브 2분, 로컬 5초
const MIN_JITTER = 1000; // 1초
const MAX_JITTER = 15000; // 15초

// 중복 접속 이벤트 타입 정의
type DuplicateLoginEvent = {
  type: 'duplicate-login';
};

const getRandomInterval = () => {
  const jitter =
    Math.floor(Math.random() * (MAX_JITTER - MIN_JITTER + 1)) + MIN_JITTER;
  return PING_INTERVAL + jitter;
};

export function useDuplicateLoginDetector<T extends (...args: any[]) => any>({
  user,
  onDetect,
}: {
  user: UserInfo | null;
  onDetect: T;
}) {
  const router = useRouter();
  const [delay, setDelay] = useState<number | null>(null);
  const channelRef = useRef<BroadcastChannel<DuplicateLoginEvent> | null>(null);

  // BroadcastChannel 생성
  useEffect(() => {
    const channel = new BroadcastChannel<DuplicateLoginEvent>(
      DUPLICATE_LOGIN_BROADCAST_CHANNEL,
    );
    channelRef.current = channel;

    const handleDuplicateLoginEvent = (event: DuplicateLoginEvent) => {
      if (event.type === 'duplicate-login') {
        onDetect({
          redirectTo: `/duplicate-login?callbackUrl=${encodeURIComponent(
            router.asPath,
          )}`,
        });
        setDelay(null);
      }
    };

    channel.onmessage = handleDuplicateLoginEvent;

    return () => {
      void channel.close();
      channelRef.current = null;
    };
  }, [router.asPath, onDetect]);

  // user가 로그인/로그아웃될 때마다 delay 재설정
  useEffect(() => {
    const isDevActivated =
      isDevelopment &&
      safeLocalStorage.get(DUPLICATE_LOGIN_ACTIVATED_KEY) === 'true';

    const shouldActivate = isProduction || isStage || isDevActivated;

    if (user?.quizUserId && shouldActivate) {
      setDelay(getRandomInterval());
    } else {
      setDelay(null);
    }
  }, [user?.quizUserId]);

  // interval마다 핑쏘기
  useInterval(
    () => {
      void (async () => {
        if (!user?.quizUserId) return;

        try {
          const userSessionKey = safeLocalStorage.get(SESSION_KEY);
          await ping({
            userId: user.quizUserId,
            sessionKey: userSessionKey || '',
          });
          // 다음 ping 간격을 랜덤하게 설정
          setDelay(getRandomInterval());
        } catch (error) {
          if (isZepQuizApiError(error) && error.status === 'SESSION_EXPIRED') {
            void channelRef.current?.postMessage({
              type: 'duplicate-login',
            });

            onDetect({
              redirectTo: `/duplicate-login?callbackUrl=${encodeURIComponent(
                router.asPath,
              )}`,
            });
            setDelay(null);
          }
        }
      })();
    },
    { delay },
  );
}
